import { get, isEmpty } from 'lodash';
import moment from 'moment';
import {
  convertObjToCamelCase,
  retrieveDataMapper,
  retrieveObjectMapper,
} from '../..';

export const transformEmergencyContactInfo = (contactDetails = {}) =>
  retrieveObjectMapper(contactDetails, [
    'firstName',
    'lastName',
    'relationship',
    'identificationNo',
    'postalCode',
    'address',
    'floorNo',
    'unitNo',
    'mobilePhone',
    'mobilePhoneCC',
    'blockNo',
    'email',
  ]);

export const transformListAllEnrolledChildDocuments = ({
  resp,
  parents = {},
  regParents = {},
  users = {},
  documentTypeConfigs = {},
  documentTags = [],
  documents = {},
  familyMembers = [],
}) => {
  return {
    data: {
      listAllEnrolledChildDocuments: {
        data: (get(resp, 'data.data') || []).map(item => ({
          ...convertObjToCamelCase(item, [], ['status:status_string']),
          parent: retrieveDataMapper(parents, item?.fk_parent, [
            'ID:id',
            'lastname',
            'firstname',
            'identification_no',
          ]),
          registrationParent: retrieveDataMapper(
            regParents,
            item?.fk_registration_parent,
            ['ID:id', 'fk_parent', 'lastname', 'firstname', 'identification_no']
          ),
          userByReviewedBy: retrieveDataMapper(users, item?.reviewed_by, [
            'ID:id',
            'firstname',
            'lastname',
          ]),
          documentTypeConfig: retrieveDataMapper(
            documentTypeConfigs,
            item?.fk_document_type_config,
            ['label', 'code']
          ),
          registrationFamilyMember: retrieveDataMapper(
            familyMembers,
            item?.fk_registration_child,
            ['ID:id', 'lastname', 'firstname', 'identification_no'],
            'fk_registration_child'
          ),
          documentTags: {
            data: documentTags.map(docTag => ({
              ...convertObjToCamelCase(docTag),
              document: {
                ...retrieveDataMapper(documents, docTag?.fk_document, [
                  'ID:id',
                  'created_at',
                  'updated_at',
                  'filename',
                  'uRL:url',
                  'preSignedUrl:url_presigned_url',
                  'uploaded_by',
                ]),
                user: retrieveDataMapper(users, docTag?.uploaded_by, [
                  'ID:id',
                  'firstname',
                  'lastname',
                ]),
              },
            })),
          },
        })),
        totalCount: get(resp, 'data.totalCount', 0),
      },
    },
    success: true,
  };
};

export const transformChildProfileByID = ({
  child = {},
  currentClass = {},
  classTeachersData = [],
  classTeachers = [],
  levelData = null,
}) => {
  return {
    ...retrieveObjectMapper(child, [
      'ID:id',
      'firstname',
      'lastname',
      'imageKey:image_key',
      'imageKeyPresignedURL:image_key_presigned_url',
      'dateOfBirth:date_of_birth',
    ]),
    currentClass: currentClass?.label
      ? {
          class: {
            ...retrieveObjectMapper(currentClass, ['ID:id', 'label']),
            classTeachers: {
              data:
                classTeachersData.map(ct => ({
                  ID: ct.id,
                  user: classTeachers.find(t => t.id === ct.fk_teacher)
                    ? retrieveObjectMapper(
                        classTeachers.find(t => t.id === ct.fk_teacher),
                        ['ID:id', 'firstname', 'lastname']
                      )
                    : {},
                })) || [],
            },
          },
        }
      : null,
    currentLevel: levelData
      ? {
          level: {
            ...retrieveObjectMapper(levelData, ['ID:id', 'label', 'code']),
          },
        }
      : null,
  };
};

export const transformChildByID = ({
  data,
  childLevels = [],
  levels = [],
  centres = {},
  activityLogs = [],
  enrolmentStatuses = {},
  closestLevels = [],
  IDChild,
  programs = [],
  nextLevels = [],
  withdrawls = [],
  childRelations = [],
  users = [],
  parents = [],
  addresses = [],
  childCurrentLevelAndCentre = {},
  lastChildLevel = {},
  upcomingChildLevels = {},
  currentClasses = {},
  upcomingClasses = {},
  classes = [],
  bankAccounts = {},
  tradeDebtNotificationConfig = {},
  forms = [],
  formParticipants = {},
  userRoles = {},
  temporaryWithdrawals = {},
  withdrawalDraftItems = {},
  nationalityUpdateEffectiveFrom,
  supportSchemaUpdateEffectiveFrom,
  childCurrentEstimatedPromotionDate,
  enrolmentDate,
  programTypeUpdateEffectiveFrom,
  childSiblings = {},
  ecdaRegistrations = {},
}) => {
  const enrolledChildrenData = get(childSiblings, 'EnrolledChildren', {});
  const registrationChildrenData = get(
    childSiblings,
    'RegistrationChildren',
    {}
  );

  const getRetations = (
    childRelationsData = [],
    type = '',
    transormer = () => {}
  ) => {
    if (!type || !childRelationsData.length) return [];
    const relations = childRelationsData.filter(cr => cr.type_string === type);
    return {
      data: relations.map(r => ({
        ...convertObjToCamelCase(r, [], ['type:type_string', 'ID:id']),
        ...transormer?.(r),
      })),
      totalCount: relations.length,
    };
  };

  return {
    success: true,
    data: {
      childByID: {
        enrolmentDate,
        nationalityUpdateEffectiveFrom,
        supportSchemaUpdateEffectiveFrom,
        childCurrentEstimatedPromotionDate,
        programTypeUpdateEffectiveFrom,
        ...convertObjToCamelCase(
          data?.data,
          [],
          [
            'imageKeyPresignedURL:image_key_presigned_url',
            'ID:id',
            'defaultPaymentMode:default_payment_mode_string',
          ]
        ),
        childLevels: {
          data: childLevels.map(childLevel => ({
            ...convertObjToCamelCase(childLevel),
            level: retrieveDataMapper(levels, childLevel?.fk_level, [
              'ID:id',
              'label',
            ]),
            centre: retrieveDataMapper(centres?.data, childLevel?.fk_centre, [
              'ID:id',
              'label',
            ]),
          })),
        },
        childActivityLogs: {
          data: activityLogs.map(childActivityLog =>
            convertObjToCamelCase(childActivityLog)
          ),
        },
        enrolmentStatus: retrieveDataMapper(enrolmentStatuses, IDChild, [
          'status',
          'label',
        ]),
        closestLevel: (() => {
          const closestLevel = retrieveDataMapper(
            closestLevels,
            IDChild,
            [
              'from',
              'to',
              'move_reason',
              'fk_level',
              'fk_centre',
              'fk_program',
            ],
            'fk_child'
          );
          if (!closestLevel) return null;
          return {
            ...closestLevel,
            centre: retrieveDataMapper(centres?.data, closestLevel?.fkCentre, [
              'ID:id',
              'label',
            ]),
            level: retrieveDataMapper(levels, closestLevel?.fkLevel, [
              'ID:id',
              'label',
              'code',
            ]),
            program: retrieveDataMapper(programs, closestLevel?.fkProgram, [
              'ID:id',
              'label',
            ]),
            nextLevel: retrieveDataMapper(nextLevels, closestLevel?.fkLevel, [
              'ID:id',
              'label',
              'code',
            ]),
          };
        })(),
        withdrawals: {
          data: withdrawls.map(w => ({
            ...convertObjToCamelCase(w),
            centre: retrieveDataMapper(centres?.data, w?.fk_centre, ['ID:id']),
          })),
        },
        familyMembers: getRetations(childRelations, 'family_member'),
        mainApplicant: getRetations(childRelations, 'applicant', d => ({
          user: {
            ...convertObjToCamelCase(users.find(u => u.id === d?.fk_user)),
            parent: {
              ...convertObjToCamelCase(
                parents.find(p => p.fk_user === d?.fk_user)
              ),
            },
            addresses: {
              data: (get(addresses, `${d?.fk_user}.data`) || []).map(address =>
                convertObjToCamelCase(address)
              ),
            },
          },
          parent: (() => {
            const parent = convertObjToCamelCase(
              parents.find(p => p.id === d?.fk_parent)
            );
            return {
              ...parent,
              addresses: {
                data: (get(addresses, `${parent?.fkUser}.data`) || []).map(
                  address => convertObjToCamelCase(address)
                ),
              },
            };
          })(),
        })),
        secondApplicant: getRetations(
          childRelations,
          'second_applicant',
          d => ({
            user: {
              ...convertObjToCamelCase(users.find(u => u.id === d?.fk_user)),
              parent: {
                ...convertObjToCamelCase(
                  parents.find(p => p.fk_user === d?.fk_user)
                ),
              },
              addresses: {
                data: (get(addresses, `${d?.fk_user}.data`) || []).map(
                  address => convertObjToCamelCase(address)
                ),
              },
            },
            parent: (() => {
              const parent = convertObjToCamelCase(
                parents.find(p => p.id === d?.fk_parent)
              );
              return {
                ...parent,
                addresses: {
                  data: (get(addresses, `${parent?.fkUser}.data`) || []).map(
                    address => convertObjToCamelCase(address)
                  ),
                },
              };
            })(),
          })
        ),
        currentLevel: {
          ...convertObjToCamelCase(
            get(childCurrentLevelAndCentre, `${IDChild}.child_level`, {})
          ),
          centre: convertObjToCamelCase(
            get(childCurrentLevelAndCentre, `${IDChild}.centre`, {})
          ),
          program: retrieveDataMapper(
            programs,
            get(
              childCurrentLevelAndCentre,
              `${IDChild}.child_level.fk_program`
            ),
            ['ID:id', 'label']
          ),
          level: retrieveDataMapper(
            levels,
            get(childCurrentLevelAndCentre, `${IDChild}.child_level.fk_level`),
            ['ID:id', 'label', 'code']
          ),
          nextLevel: retrieveDataMapper(
            nextLevels,
            get(childCurrentLevelAndCentre, `${IDChild}.child_level.fk_level`),
            ['ID:id', 'label', 'code']
          ),
        },
        lastLevel: {
          ...convertObjToCamelCase(lastChildLevel),
          centre: retrieveDataMapper(centres?.data, lastChildLevel?.fk_centre, [
            'ID:id',
            'label',
          ]),
        },
        upcomingLevel: {
          ...convertObjToCamelCase(get(upcomingChildLevels, IDChild, {})),
          centre: retrieveDataMapper(
            centres?.data,
            get(upcomingChildLevels, `${IDChild}.fk_centre`),
            ['ID:id', 'label']
          ),
          program: retrieveDataMapper(
            programs,
            get(upcomingChildLevels, `${IDChild}.fk_program`),
            ['ID:id', 'label']
          ),
          level: retrieveDataMapper(
            levels,
            get(upcomingChildLevels, `${IDChild}.fk_level`),
            ['ID:id', 'label', 'code']
          ),
          forms: {
            data: forms?.map(form => ({
              ...convertObjToCamelCase(form),
              formParticipants: {
                data: get(formParticipants, `${form.id}.data`, []).map(fp => ({
                  ...convertObjToCamelCase(
                    fp,
                    [],
                    ['status:status_string', 'ID:id']
                  ),
                  user: (() => {
                    const user = retrieveDataMapper(users, fp?.fk_user, [
                      'ID:id',
                      'firstname',
                      'lastname',
                    ]);

                    if (!user) return null;

                    return {
                      ...user,
                      getRole: retrieveDataMapper(userRoles, user?.ID, [
                        'label',
                      ]),
                    };
                  })(),
                  parent: retrieveDataMapper(parents, fp?.fk_parent, [
                    'ID:id',
                    'firstname',
                    'lastname',
                  ]),
                })),
              },
            })),
          },
        },
        currentClass: {
          ...retrieveObjectMapper(get(currentClasses, IDChild), ['from', 'to']),
          class: {
            ...convertObjToCamelCase(get(currentClasses, IDChild, {})),
            level: retrieveDataMapper(
              levels,
              get(currentClasses, `${IDChild}.fk_level`),
              ['ID:id', 'label']
            ),
            centre: retrieveDataMapper(
              centres?.data,
              get(currentClasses, `${IDChild}.fk_centre`),
              ['ID:id', 'label']
            ),
          },
        },
        upcomingClass: {
          ...retrieveObjectMapper(get(upcomingClasses, IDChild), [
            'from',
            'to',
          ]),
          class: (() => {
            const classObj = retrieveDataMapper(
              classes,
              get(upcomingClasses, `${IDChild}.fk_class`),
              ['ID:id', 'label', 'fk_level', 'fk_centre']
            );
            return {
              ...classObj,
              level: retrieveDataMapper(levels, classObj?.fkLevel, [
                'ID:id',
                'label',
              ]),
              centre: retrieveDataMapper(centres?.data, classObj?.fkCentre, [
                'ID:id',
                'label',
              ]),
            };
          })(),
        },
        withdrawalDraftItems: {
          data: (get(withdrawalDraftItems, 'data') || []).map(dr =>
            convertObjToCamelCase(dr, [], ['ID:id', 'status:status_string'])
          ),
        },
        temporaryWithdrawals: {
          data: (get(temporaryWithdrawals, 'data') || []).map(tmp =>
            convertObjToCamelCase(tmp, [], ['ID:id', 'status:status_string'])
          ),
          totalCount: get(temporaryWithdrawals, 'totalCount', 0),
        },
        childAuthorisedPersonals: null, // authorised personals is not using anymore
        enquiries: {
          data: [], // Leave empty because enquiry is not using anymore
        },
        tradeDebtNotificationConfig: retrieveObjectMapper(
          tradeDebtNotificationConfig,
          ['notificatioEnabled']
        ),
        sibling: {
          EnrolledChildren: {
            data: (get(enrolledChildrenData, 'data') || []).map(
              enrolledChild => ({
                ...convertObjToCamelCase(
                  enrolledChild,
                  [],
                  ['ID:id', 'defaultPaymentMode:default_payment_mode_string']
                ),
                currentLevel: {
                  centre: retrieveObjectMapper(
                    get(
                      childCurrentLevelAndCentre,
                      `${enrolledChild?.id}.centre`,
                      {}
                    ),
                    ['label']
                  ),
                  program: retrieveDataMapper(
                    programs,
                    get(
                      childCurrentLevelAndCentre,
                      `${enrolledChild?.id}.child_level.fk_program`
                    ),
                    ['label']
                  ),
                },
                upcomingLevel: {
                  centre: retrieveDataMapper(
                    centres?.data,
                    get(upcomingChildLevels, `${enrolledChild?.id}.fk_centre`),
                    ['label']
                  ),
                  program: retrieveDataMapper(
                    programs,
                    get(upcomingChildLevels, `${enrolledChild?.id}.fk_program`),
                    ['label']
                  ),
                },
                enrolmentStatus: retrieveDataMapper(
                  enrolmentStatuses,
                  enrolledChild?.id,
                  ['status', 'label']
                ),
              })
            ),
            totalCount: get(enrolledChildrenData, 'totalCount', 0),
          },
          RegistrationChildren: {
            data: (get(registrationChildrenData, 'data') || []).map(
              registrationChild => ({
                ...convertObjToCamelCase(registrationChild, [], ['ID:id']),
                ecdaRegistrations: {
                  data: (
                    get(ecdaRegistrations, registrationChild.id) || []
                  ).map(ecdaReg => ({
                    ...convertObjToCamelCase(
                      ecdaReg,
                      [],
                      [
                        'ID:id',
                        'giroStatus:giro_status_string',
                        'paymentStatus:payment_status_string',
                        'state:state_string',
                        'type:type_string',
                        'documentStatus:document_status_string',
                      ]
                    ),
                    centre: retrieveDataMapper(
                      centres?.data,
                      ecdaReg?.fk_centre,
                      ['label']
                    ),
                    program: retrieveDataMapper(programs, ecdaReg?.fk_program, [
                      'label',
                    ]),
                  })),
                },
              })
            ),
            totalCount: get(registrationChildrenData, 'totalCount', 0),
          },
        },
        bankAccounts: {
          data: (get(bankAccounts, 'data') || []).map(bankAccount =>
            convertObjToCamelCase(
              bankAccount,
              [],
              [
                'ID:id',
                'status:status_string',
                'accountType:account_type_string',
                'source:source_string',
              ]
            )
          ),
        },
      },
    },
  };
};

export const transformPastChildren = ({
  data = [],
  closestLevel = [],
  enrolmentStatus = {},
  upcomingClasses = [],
}) =>
  data?.map(dt => {
    const child = dt?.child;
    const center = dt?.centre;
    const childClosestLevel = closestLevel?.find(
      cL => cL?.fk_child === dt?.child?.id
    );
    const upcomingClass = upcomingClasses?.find(
      uC => uC?.fk_child === dt?.child?.id
    );

    return {
      child: child && {
        ...convertObjToCamelCase(child, [
          'imageKeyPresignedURL:image_key_presigned_url',
        ]),
        upcomingClass: upcomingClass && convertObjToCamelCase(upcomingClass),
        closestLevel:
          childClosestLevel && convertObjToCamelCase(childClosestLevel),
        enrolmentStatus: enrolmentStatus?.[dt?.child?.id] || null,
      },
      centre: center && convertObjToCamelCase(center),
      enrolmentDate: dt?.enrolmentDate || null,
      withdrawnDate: dt?.withdrawnDate || null,
    };
  }) || [];

export const transfromFetchAllChild = ({
  data = [],
  enrollmentStatus = {},
  currentClass = {},
  upcomingClass = {},
  currentLevel = {},
  upcomingLevel = {},
  levels = [],
  centres = {},
  nextLevels = [],
  promotionData = {},
  childData,
}) => {
  return {
    data: {
      findAllChild: {
        totalCount: data?.length || 0,
        data: data?.map(ch => {
          const childEnrollmentStatus = enrollmentStatus[`${ch?.id}`];
          const childCurrentClass = get(currentClass, `data.data[${ch?.id}]`);
          const childCurrentClassLevel = levels.find(
            l => l.id === get(childCurrentClass, 'fk_level')
          );
          const childCurrentClassCentre = get(centres, 'data', []).find(
            l => l.id === get(childCurrentClass, 'fk_centre')
          );
          const childFirstEnrolmentDate = childData.find(
            cd => cd.id === ch?.id
          );
          const childUpcomingClass = get(upcomingClass, `data.data[${ch?.id}]`);
          const childUpcomingClassLevel = levels.find(
            l => l.id === get(childUpcomingClass, 'fk_level')
          );
          const childUpcomingClassCentre = get(centres, 'data', []).find(
            l => l.id === get(childUpcomingClass, 'fk_centre')
          );

          const childCurrentLevel = currentLevel[`${ch?.id}`];
          const childCurrentLevelCentre = get(centres, 'data', []).find(
            l => l.id === get(childCurrentLevel, 'fk_centre')
          );

          const childUpcomingLevel = upcomingLevel[`${ch?.id}`];
          const childUpcomingLevelCentre = get(centres, 'data', []).find(
            l => l.id === get(childUpcomingLevel, 'fk_centre')
          );
          const promotionDateDetails = get(promotionData, 'results', []).find(
            pd => pd?.child_id === ch?.id
          );
          return {
            ...convertObjToCamelCase(ch, [
              'imageKeyPresignedURL:image_key_presigned_url',
            ]),
            enrolmentStatus: {
              status: get(childEnrollmentStatus, 'status'),
              label: get(childEnrollmentStatus, 'label'),
            },
            firstEnrolmentDate: get(
              childFirstEnrolmentDate,
              'first_enrolment_date',
              null
            ),
            childCurrentEstimatedPromotionDate: get(
              promotionDateDetails,
              'time'
            )
              ? moment(get(promotionDateDetails, 'time')).format(
                  'YYYY-MM-DD HH:mm:ss'
                )
              : null,
            currentClass: childCurrentClass?.id
              ? {
                  class: {
                    ID: get(childCurrentClass, 'id'),
                    label: get(childCurrentClass, 'label'),
                    level: childCurrentClassLevel?.id
                      ? {
                          label: get(childCurrentClassLevel, 'label'),
                        }
                      : null,
                    centre: childCurrentClassCentre?.id
                      ? {
                          ID: get(childCurrentClassCentre, 'id'),
                          label: get(childCurrentClassCentre, 'label'),
                        }
                      : null,
                  },
                  from: get(childCurrentClass, 'from', null),
                }
              : null,
            upcomingClass: childUpcomingClass?.id
              ? {
                  class: {
                    ID: get(childUpcomingClass, 'id'),
                    label: get(childUpcomingClass, 'label'),
                    level: childUpcomingClassLevel?.id
                      ? {
                          label: get(childUpcomingClassLevel, 'label'),
                        }
                      : null,
                    centre: childUpcomingClassCentre?.id
                      ? {
                          ID: get(childUpcomingClassCentre, 'id'),
                          label: get(childUpcomingClassCentre, 'label'),
                        }
                      : null,
                  },
                  from: get(childUpcomingClass, 'from', null),
                }
              : null,
            currentLevel: childCurrentLevel?.id
              ? {
                  from: get(childCurrentLevel, 'from', null),
                  to: get(childCurrentLevel, 'to', null),
                  moveReason: get(childCurrentLevel, 'move_reason', null),
                  level: {
                    ID: get(childCurrentLevel, 'id'),
                    label: get(childCurrentLevel, 'label'),
                    code: get(childCurrentLevel, 'code'),
                    nextLevel: retrieveDataMapper(
                      nextLevels,
                      get(childCurrentLevel, 'id'),
                      ['ID:id', 'label', 'code']
                    ),
                  },
                  centre: {
                    ID: get(childCurrentLevelCentre, 'id'),
                    label: get(childCurrentLevelCentre, 'label'),
                    code: get(childCurrentLevelCentre, 'code'),
                  },
                  nextLevel: retrieveDataMapper(
                    nextLevels,
                    get(childCurrentLevel, 'id'),
                    ['ID:id', 'label', 'code']
                  ),
                }
              : null,
            upcomingLevel: childUpcomingLevel?.id
              ? {
                  level: {
                    ID: get(childUpcomingLevel, 'id'),
                    label: get(childUpcomingLevel, 'label'),
                    code: get(childUpcomingLevel, 'code'),
                    nextLevel: retrieveDataMapper(
                      nextLevels,
                      get(childUpcomingLevel, 'id'),
                      ['ID:id', 'label', 'code']
                    ),
                  },
                  centre: {
                    ID: get(childUpcomingLevelCentre, 'id'),
                    label: get(childUpcomingLevelCentre, 'label'),
                    code: get(childUpcomingLevelCentre, 'code'),
                  },
                  nextLevel: retrieveDataMapper(
                    nextLevels,
                    get(childUpcomingLevel, 'id'),
                    ['ID:id', 'label', 'code']
                  ),
                }
              : null,
          };
        }),
      },
    },
  };
};

export const transformAllWithdrawalDrafts = ({
  msData = [],
  centres = {},
  children = {},
  enrolmentStatuses = {},
  closestLevels = [],
  withdrawalDraftItems = {},
}) => {
  return msData.map(item => {
    const fkChildId = get(item, 'fk_child');
    const fkCentreId = get(item, 'fk_centre');
    const draftId = get(item, 'id');

    return {
      ...retrieveObjectMapper(item, [
        'ID:id',
        'fkChild:fk_child',
        'status:status_string',
        'additionalStatus:additional_status_string',
        'eConsentStatus:e_consent_status_string',
      ]),
      child: {
        ...retrieveDataMapper(children, fkChildId, [
          'ID:id',
          'firstname',
          'lastname',
          'birthCertificate:birth_certificate',
        ]),
        enrolmentStatus: retrieveDataMapper(enrolmentStatuses, fkChildId, [
          'status',
          'label',
        ]),
        closestLevel: retrieveDataMapper(
          closestLevels,
          fkChildId,
          ['fkLevel:fk_level', 'fkProgram:fk_program'],
          'fk_child'
        ),
      },
      centre: retrieveDataMapper(get(centres, 'data', []), fkCentreId, [
        'ID:id',
        'label',
      ]),
      withdrawalDraftItem: retrieveDataMapper(withdrawalDraftItems, draftId, [
        'refundDepositAmount:refund_deposit_amount',
        'refundFeeAmount:refund_fee_amount',
        'createdAt:created_at',
        'effectiveDate:effective_date',
        'submissionDate:submission_date',
      ]),
    };
  });
};

export const transformWithdrawalDraftsForGraphQL = ({
  msData = [],
  withdrawalDraftItemsByIds = {},
  childrenById = {},
  formsByDraftId = {},
  formParticipants = {},
  codesById = [],
  usersById = {},
  userRoles = {},
  refundAmountsByDraftId = {},
  activityLogsByDraftId = {},
}) => {
  return msData.map(item => {
    const draftId = get(item, 'id');
    const childId = get(item, 'fk_child');
    const withdrawalDraftItem = withdrawalDraftItemsByIds[draftId] || {};
    const refundAmountData = get(
      refundAmountsByDraftId,
      `${draftId}.refundAmount`
    );

    const form = get(formsByDraftId, draftId) || null;
    const formId = form ? get(form, 'id') : null;
    const participants =
      formId && !isEmpty(formParticipants)
        ? get(formParticipants, `${formId}.data`, [])
        : [];

    const reasonId = get(withdrawalDraftItem, 'reason');
    const parentReasonId = get(withdrawalDraftItem, 'parent_reason');

    const transformedParticipants = participants.map(participant => {
      const userId = get(participant, 'fk_user');
      const user = retrieveDataMapper(usersById, userId, [
        'ID:id',
        'firstname',
        'lastname',
      ]);
      const roleData = get(userRoles, 'data') || userRoles;
      const role = retrieveDataMapper(roleData, userId, ['label']);

      return {
        ...convertObjToCamelCase(
          participant,
          [],
          [
            'ID:id',
            'status:status_string',
            'email',
            'createdAt:created_at',
            'updatedAt:updated_at',
            'fkParent:fk_parent',
          ]
        ),
        user: user
          ? {
              firstname: get(user, 'firstname'),
              lastname: get(user, 'lastname'),
              getRole: role
                ? {
                    label: get(role, 'label'),
                  }
                : null,
            }
          : null,
      };
    });

    return {
      ...retrieveObjectMapper(item, [
        'ID:id',
        'fkSchool:fk_school',
        'fkCentre:fk_centre',
        'status:status_string',
        'eConsentStatus:e_consent_status_string',
        'updatedAt:updated_at',
        'additionalStatus:additional_status_string',
      ]),
      forms: form
        ? {
            data: {
              formParticipants: {
                data: transformedParticipants,
              },
            },
          }
        : null,
      estimateRefundAmount: {
        defaultDepositRefundAmount: get(
          refundAmountData,
          'defaultDepositRefundAmount',
          null
        ),
        defaultFeeRefundAmount: get(
          refundAmountData,
          'defaultFeeRefundAmount',
          null
        ),
        monthlyFee: get(refundAmountData, 'monthlyFee', null),
        depositAmount: get(refundAmountData, 'depositAmount', null),
      },
      withdrawalDraftItem: withdrawalDraftItem
        ? {
            ...convertObjToCamelCase(
              withdrawalDraftItem,
              [],
              [
                'refundFeeAmount:refund_fee_amount',
                'refundDepositAmount:refund_deposit_amount',
                'effectiveDate:effective_date',
                'createdAt:created_at',
                'submissionDate:submission_date',
                'otherReason:other_reason',
              ]
            ),
            child: retrieveDataMapper(childrenById, childId, [
              'ID:id',
              'firstname',
              'lastname',
            ]),
            codeByReason: retrieveDataMapper(codesById, reasonId, [
              'ID:id',
              'label',
              'description',
            ]),
            codeByParentReason: retrieveDataMapper(codesById, parentReasonId, [
              'ID:id',
              'label',
              'description',
            ]),
          }
        : null,
      withdrawalDraftActivityLogs: {
        data:
          activityLogsByDraftId[draftId]?.map(log => ({
            ID: get(log, 'id'),
            remark: get(log, 'remark'),
          })) || [],
      },
    };
  });
};

export const transformChildrenData = ({
  children = [],
  enrolmentStatus = {},
  currentClasses = {},
  upcomingClasses,
  currentLevels,
  upcomingLevels,
  closestLevels,
}) =>
  children?.map(ch => {
    const upcomingClass = upcomingClasses?.find(uC => uC?.fk_child === ch?.id);
    const closestLevel = closestLevels?.find(uC => uC?.fk_child === ch?.id);
    return {
      ...convertObjToCamelCase(ch, [
        'imageKeyPresignedURL:image_key_presigned_url',
      ]),
      enrolmentStatus: enrolmentStatus?.[ch?.id] || null,
      currentClass: currentClasses?.[ch?.id]
        ? {
            class: currentClasses?.[ch?.id]
              ? {
                  ...convertObjToCamelCase(currentClasses?.[ch?.id]),
                  level: currentClasses?.[ch?.id]?.level
                    ? {
                        ...convertObjToCamelCase(
                          currentClasses?.[ch?.id]?.level
                        ),
                      }
                    : null,
                  centre: currentClasses?.[ch?.id]?.centre
                    ? {
                        ...convertObjToCamelCase(
                          currentClasses?.[ch?.id]?.centre
                        ),
                      }
                    : null,
                }
              : null,
            from: currentClasses?.[ch?.id]?.from || null,
          }
        : null,
      upcomingClass: upcomingClass
        ? convertObjToCamelCase(upcomingClass)
        : null,
      currentLevel: currentLevels?.[ch?.id]
        ? convertObjToCamelCase(currentLevels?.[ch?.id])
        : null,
      upcomingLevel: upcomingLevels?.[ch?.id]
        ? convertObjToCamelCase(upcomingLevels?.[ch?.id])
        : null,
      closestLevel: closestLevel ? convertObjToCamelCase(closestLevel) : null,
      enquiries: {
        // Missing subquery.
        data: [],
      },
    };
  }) || [];

export const msTransformChildAssignToClassData = ({
  resp = {},
  enrolmentStatusData = {},
  currentClasses = [],
  upcomingClassData,
  currentLevelData,
  upcomingLevelData,
  childData = [],
  childCurrentEstimatedPromotionDateData = {},
  enrolmentDateData = {},
  closestLevelData,
  childRelationsData,
  parentData,
  childLevelData = {},
  centreData,
  levelData,
  nextLevelData,
  programData,
}) => {
  return {
    data: {
      getChildrenToAssignToClass: {
        totalCount: get(resp, 'data.totalCount', 0),
        data: get(resp, 'data.data', []).map(item => {
          const closestLevel = closestLevelData?.find(
            cl => cl?.fk_child === item?.id
          );
          const closestCentreDetails = get(centreData, 'data', []).find(
            cl => cl?.id === closestLevel?.fk_centre
          );
          const closestLevelDetails = levelData.find(
            cl => cl?.id === closestLevel?.fk_level
          );
          const closestProgramDetails = programData.find(
            cl => cl?.id === closestLevel?.fk_program
          );
          const childFirstEnrolmentDate = childData.find(
            ch => ch?.id === item?.id
          );
          const childRelation = childRelationsData?.find(
            cr => cr?.child_id === item?.id
          );
          const childLevel = get(childLevelData, 'data', []).find(
            cl => cl.fk_child === item?.id
          );
          const childLevelProgram = programData.find(
            cl => cl?.id === childLevel?.fk_program
          );
          const childCurrentEstimatedPromotionDate = get(
            childCurrentEstimatedPromotionDateData,
            'results',
            []
          ).find(ch => ch.child_id === item?.id);
          const currentClassCentreDetails = get(centreData, 'data', []).find(
            cl => cl?.id === currentClasses?.[item?.id]?.fk_centre
          );
          const currentClassLevelDetails = levelData.find(
            cl => cl?.id === currentClasses?.[item?.id]?.fk_level
          );
          const currentLevelCentreDetails = get(centreData, 'data', []).find(
            cl => cl?.id === currentLevelData?.[item?.id]?.fk_centre
          );
          const currentLevelDetails = currentLevelData?.[item?.id];
          const upcomingLevelDetails = upcomingLevelData?.[item?.id];
          const upcomingLevelProgramDetails = programData.find(
            cl => cl?.id === upcomingLevelDetails?.fk_program
          );
          const upcomingLevelChildDetails = levelData.find(
            cl => cl?.id === upcomingLevelDetails?.fk_level
          );
          const upcomingLevelCentreDetails = get(centreData, 'data', []).find(
            cl => cl?.id === upcomingLevelDetails?.fk_centre
          );
          return {
            ...convertObjToCamelCase(item, [
              'imageKeyPresignedURL:image_key_presigned_url',
            ]),
            enrolmentStatus: enrolmentStatusData?.[item?.id] || null,
            firstEnrolmentDate: get(
              childFirstEnrolmentDate,
              'first_enrolment_date',
              null
            ),
            enrolmentDate: get(enrolmentDateData, `data[${item.id}]`, null),
            childCurrentEstimatedPromotionDate: get(
              childCurrentEstimatedPromotionDate,
              'time'
            )
              ? moment(get(childCurrentEstimatedPromotionDate, 'time')).format(
                  'YYYY-MM-DD HH:mm:ss'
                )
              : null,
            currentClass: currentClasses?.[item?.id]
              ? {
                  class: currentClasses?.[item?.id]
                    ? {
                        ...convertObjToCamelCase(currentClasses?.[item?.id]),
                        level: currentClassLevelDetails?.id
                          ? {
                              ...convertObjToCamelCase(
                                currentClassLevelDetails
                              ),
                            }
                          : null,
                        centre: currentClassCentreDetails?.id
                          ? {
                              ...convertObjToCamelCase(
                                currentClassCentreDetails
                              ),
                            }
                          : null,
                      }
                    : null,
                  from: currentClasses?.[item?.id]?.from || null,
                }
              : null,
            upcomingClass: upcomingClassData?.[item?.id]
              ? {
                  class: upcomingClassData?.[item?.id]
                    ? {
                        ...convertObjToCamelCase(upcomingClassData?.[item?.id]),
                      }
                    : null,
                  centre: {}, // pending from be
                  level: {}, // pending from be
                  from: upcomingClassData?.[item?.id]?.from || null,
                }
              : null,
            currentLevel: currentLevelData?.[item?.id]
              ? {
                  nextLevel: nextLevelData?.[currentLevelDetails?.id]?.id
                    ? {
                        ...convertObjToCamelCase(
                          nextLevelData?.[currentLevelDetails?.id]
                        ),
                      }
                    : null,

                  level: currentLevelData?.[item?.id]
                    ? {
                        ...convertObjToCamelCase(currentLevelData?.[item?.id]),
                        nextLevel: nextLevelData?.[currentLevelDetails?.id]?.id
                          ? {
                              ...convertObjToCamelCase(
                                nextLevelData?.[currentLevelDetails?.id]
                              ),
                            }
                          : null,
                      }
                    : null,
                  centre: currentLevelCentreDetails?.id
                    ? {
                        ...convertObjToCamelCase(currentLevelCentreDetails),
                      }
                    : null,
                  program: {}, // pending from be
                  from: currentLevelData?.[item?.id]?.from || null,
                  to: currentLevelData?.[item?.id]?.to || null,
                  moveReason: currentLevelData?.[item?.id]?.move_reason || null,
                }
              : null,
            upcomingLevel: upcomingLevelData?.[item?.id]
              ? {
                  nextLevel: nextLevelData?.[upcomingLevelChildDetails?.id]
                    ? {
                        ...convertObjToCamelCase(
                          nextLevelData?.[upcomingLevelChildDetails?.id]
                        ),
                      }
                    : null,
                  level: upcomingLevelChildDetails?.id
                    ? {
                        ...convertObjToCamelCase(upcomingLevelChildDetails),
                        nextLevel: nextLevelData?.[
                          upcomingLevelChildDetails?.id
                        ]
                          ? {
                              ...convertObjToCamelCase(
                                nextLevelData?.[upcomingLevelChildDetails?.id]
                              ),
                            }
                          : null,
                      }
                    : null,
                  centre: upcomingLevelCentreDetails?.id
                    ? {
                        ...convertObjToCamelCase(upcomingLevelCentreDetails),
                      }
                    : null,
                  program: upcomingLevelProgramDetails?.id
                    ? {
                        ...convertObjToCamelCase(upcomingLevelProgramDetails),
                      }
                    : null,
                  from: upcomingLevelChildDetails?.from || null,
                }
              : null,
            enquiries: {},
            closestLevel: closestLevel
              ? {
                  ...convertObjToCamelCase(closestLevel),
                  level: closestLevelDetails?.id
                    ? {
                        ...convertObjToCamelCase(closestLevelDetails),
                      }
                    : null,
                  centre: closestCentreDetails?.id
                    ? {
                        ...convertObjToCamelCase(closestCentreDetails),
                      }
                    : null,
                  nextLevel: nextLevelData?.[closestLevelDetails?.id]
                    ? {
                        ...convertObjToCamelCase(
                          nextLevelData?.[closestLevelDetails?.id]
                        ),
                      }
                    : null,
                  program: closestProgramDetails?.id
                    ? {
                        ...convertObjToCamelCase(closestProgramDetails),
                      }
                    : null,
                }
              : null,
            childRelations:
              get(childRelation, 'data.length', 0) > 0
                ? {
                    data: get(childRelation, 'data', []).map(cr => {
                      const parentDetails = parentData.find(
                        p => p.id === cr.fk_parent
                      );
                      return {
                        parent: convertObjToCamelCase(parentDetails),
                      };
                    }),
                  }
                : null,
            childLevels: childLevel?.fk_program
              ? {
                  data: [
                    {
                      program: {
                        label: get(childLevelProgram, 'label', ''),
                      },
                    },
                  ],
                }
              : null,
          };
        }),
      },
    },
    success: true,
  };
};
